import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import {
  buildSnCRetailItemOptions,
  filterSnCBuildingOptions,
  sampleTestBlockMessage,
} from "../../../constants/sncSampleGuard";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  SNC_TOOLTIP_BY_SCREEN,
  sncTooltipGroupKey,
  type SnCStandardTooltipGroup,
} from "../../../constants/SnCTooltipInformation";
import { useTooltipText } from "../../../tooltips/TooltipContext";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

type RevenueTypeRow = {
  Building: string | null;
  "Revenue Item": string | null;
};

const emptyRevenueTypeRow = (): RevenueTypeRow => ({
  Building: null,
  "Revenue Item": null,
});

const CODE_FIELD = "Setup Code";
const NAME_FIELD = "Setup Name";

const SnCSetupItems = ({
  data,
  fieldValues,
  buildingOptions = [],
}: {
  data: (value: any) => void;
  /** Full dashboard payload — includes Retail Items Setup for Revenue Item options */
  fieldValues: any;
  /** From GET /OnBoarding/GetBuildings — "code - name" labels */
  buildingOptions?: string[];
}) => {
  const sncTipGroup = sncTooltipGroupKey("Setup Items");
  const sncTipDefaults = SNC_TOOLTIP_BY_SCREEN["Setup Items"] as SnCStandardTooltipGroup;
  const tipHeader = useTooltipText(sncTipGroup, "header", sncTipDefaults.header);
  const tipCode = useTooltipText(sncTipGroup, "code", sncTipDefaults.code);
  const tipName = useTooltipText(sncTipGroup, "name", sncTipDefaults.name);
  const tipActive = useTooltipText(sncTipGroup, "active", sncTipDefaults.active);
  const safeBuildingOptions = filterSnCBuildingOptions(buildingOptions);
  const [setupCode, setSetupCode] = useState<string | null>(null);
  const [setupName, setSetupName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [revenueTypes, setRevenueTypes] = useState<RevenueTypeRow[]>([
    emptyRevenueTypeRow(),
  ]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [codeFieldError, setCodeFieldError] = useState("");
  const [nameFieldError, setNameFieldError] = useState("");
  const [revenueTypeError, setRevenueTypeError] = useState(false);

  const revenueItemOptions = useMemo(
    () =>
      buildSnCRetailItemOptions(fieldValues?.[SnCPropertyEnum.retailItems]),
    [fieldValues?.[SnCPropertyEnum.retailItems]],
  );

  // Initialize once — do not re-sync from full payload or updatePayload loops and flickers.
  useEffect(() => {
    const list = fieldValues?.[SnCPropertyEnum.setupItems];
    if (Array.isArray(list)) {
      setItems(list);
    }
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateInput = (): boolean => {
    setCodeFieldError("");
    setNameFieldError("");
    setRevenueTypeError(false);
    if (!setupCode) {
      setCodeFieldError(CODE_FIELD + validationMessages.sncMandatorySuffix);
    }
    if (!setupName) {
      setNameFieldError(NAME_FIELD + validationMessages.sncMandatorySuffix);
    }
    // Revenue Type required once Building / Revenue Item options are wired.
    if (!setupCode || !setupName) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        setupCode,
        validationLength.sncCatalogCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(validationMessages.sncCatalogCode);
      return false;
    }
    if (
      !validateString(
        setupName,
        validationLength.sncCatalogName,
        validationType.NAME,
      )
    ) {
      setNameFieldError(validationMessages.sncCatalogName);
      return false;
    }
    const sampleBlock = sampleTestBlockMessage(
      setupCode,
      setupName,
      revenueTypes,
    );
    if (sampleBlock) {
      setAlertMessage(sampleBlock);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const payload = {
    [CODE_FIELD]: setupCode,
    [NAME_FIELD]: setupName,
    Active: active ? "Yes" : "No",
    "Revenue Types": revenueTypes.filter(
      (row) => row.Building || row["Revenue Item"],
    ),
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (checkDuplicate(otherItems, CODE_FIELD, setupCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev: any) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SnCPropertyEnum.setupItems} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(items ?? [], CODE_FIELD, setupCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev: any) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setCodeFieldError("");
      setNameFieldError("");
      setRevenueTypeError(false);
      setSetupCode(list[CODE_FIELD] ?? null);
      setSetupName(list[NAME_FIELD] ?? null);
      setActive(list.Active !== "No" && list.Active !== false);
      const savedRows = list["Revenue Types"];
      setRevenueTypes(
        Array.isArray(savedRows) && savedRows.length
          ? savedRows.map((row: any) => ({
              Building: row?.Building ?? null,
              "Revenue Item": row?.["Revenue Item"] ?? null,
            }))
          : [emptyRevenueTypeRow()],
      );
    } else {
      setSetupCode(null);
      setSetupName(null);
      setActive(true);
      setRevenueTypes([emptyRevenueTypeRow()]);
      setCodeFieldError("");
      setNameFieldError("");
      setRevenueTypeError(false);
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  const updateRevenueTypeRow = (
    index: number,
    field: keyof RevenueTypeRow,
    value: string | null,
  ) => {
    setRevenueTypes((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
    setRevenueTypeError(false);
  };

  const addRevenueTypeRow = () => {
    setRevenueTypes((prev) => [...prev, emptyRevenueTypeRow()]);
  };

  const removeRevenueTypeRow = (index: number) => {
    setRevenueTypes((prev) =>
      prev.length <= 1
        ? [emptyRevenueTypeRow()]
        : prev.filter((_, i) => i !== index),
    );
  };

  const setAllRevenueItems = () => {
    const firstRevenueItem = revenueTypes[0]?.["Revenue Item"] ?? null;
    setRevenueTypes((prev) =>
      prev.map((row) => ({ ...row, "Revenue Item": firstRevenueItem })),
    );
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Setup Items
            <Tooltip
              title={tooltipComponent(tipHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack direction="row" className="form-two-col-row" flexWrap="wrap" gap="20px">
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Setup Code *
                <Tooltip title={tooltipComponent(tipCode)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                required
                variant="outlined"
                size="small"
                placeholder="Setup Code"
                value={setupCode ?? ""}
                error={!!codeFieldError}
                helperText={codeFieldError}
                onChange={(e) => {
                  setSetupCode(e.target.value || null);
                  setCodeFieldError("");
                }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Setup Name *
                <Tooltip title={tooltipComponent(tipName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                required
                variant="outlined"
                size="small"
                placeholder="Setup Name"
                value={setupName ?? ""}
                error={!!nameFieldError}
                helperText={nameFieldError}
                onChange={(e) => {
                  setSetupName(e.target.value || null);
                  setNameFieldError("");
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" gap="2px">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active"
            />
            <Tooltip title={tooltipComponent(tipActive)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>
          <Stack direction="column" gap="12px" width="100%">
            <Stack direction="row" alignItems="center" gap="4px">
              <FormLabel component="legend" error={revenueTypeError}>
                Revenue Type *
              </FormLabel>
              <Tooltip
                title={tooltipComponent(tipHeader)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            {revenueTypeError && (
              <Typography variant="caption" color="error">
                At least one Building and Revenue Item is required
              </Typography>
            )}
            {revenueTypes.map((row, index) => (
              <Stack
                key={`setup-revenue-type-row-${index}`}
                direction="row"
                gap="10px"
                alignItems="flex-start"
                className="form-two-col-row"
                flexWrap="wrap"
              >
                <Stack direction="column" className="form-two-col-field" flex={1}>
                  <FormLabel>Building *</FormLabel>
                  <Autocomplete
                    size="small"
                    options={safeBuildingOptions}
                    value={row.Building}
                    onChange={(_, v) =>
                      updateRevenueTypeRow(index, "Building", v)
                    }
                    noOptionsText="Building options not configured yet"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select building"
                        error={revenueTypeError && !row.Building}
                      />
                    )}
                  />
                </Stack>
                <Stack direction="column" className="form-two-col-field" flex={1}>
                  <FormLabel>Revenue Item *</FormLabel>
                  <Autocomplete
                    size="small"
                    options={revenueItemOptions}
                    value={row["Revenue Item"]}
                    onChange={(_, v) =>
                      updateRevenueTypeRow(index, "Revenue Item", v)
                    }
                    noOptionsText="No retail items in Retail Items Setup"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select revenue item"
                        error={revenueTypeError && !row["Revenue Item"]}
                      />
                    )}
                  />
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ pt: "28px" }}
                  gap="4px"
                >
                  {index === 0 ? (
                    <>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={addRevenueTypeRow}
                        aria-label="Add revenue type row"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={setAllRevenueItems}
                      >
                        Set All
                      </Button>
                    </>
                  ) : (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => removeRevenueTypeRow(index)}
                      aria-label="Remove revenue type row"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Setup Item
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Setup Items Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={CODE_FIELD}
              name={NAME_FIELD}
            />
          ) : (
            <Typography variant="body2">No Setup Items Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCSetupItems;
